import {PersistentCityModel} from "@/models/PersistentCityModel";
import {toTimestamp,toMoment} from "@/dateUtils";
import {callApi, postApi} from "@/api";
import { listingRepository } from "@/data";
import {Property} from "@/models/Property";
import {User} from "@/models/User";
import ListingComment from "@/models/ListingComment";
import {
  isListingDefinitelyInCorrectCity,
  computeListingUrl,
  getListingSourceInfo,
  extractListingLatLng,
  isListingWhitelisted,
  listingMatchesQuery,
  listingIndexKeys
} from "@strenforcement/domain/listing";

export class Listing extends PersistentCityModel {

  static collectionName = "listings";

  static async searchByHost(city, host) {
    if (!host) {
      return [];
    } else {
      const response = await this.query(city, {
        constraints: [
          ['listing.host', '==', host] // TODO Can I make this case insensitive?
        ]
      });
      return response.results;
    }
  }

  constructor(city, doc) {
    super(city, doc);
    this.verifying = false;
  }

  get idFieldNameForApi() {
    return 'listingId';
  }

  get isDefinitelyInCorrectCity() {
    return isListingDefinitelyInCorrectCity({
      details: this.details,
      addresses: this.data.addresses,
      cityName: this.city?.name
    });
  }

  updateIsInCorrectCity(isInCorrectCity) {
    return this.update({
      listing: {
        isInCorrectCity,
        isInCorrectCitySetAt: new Date(),
        isInCorrectCitySetBy: this.currentUser
      }
    });
  }

  get dateLoaded() {
    return toTimestamp(this.data.dateLoaded);
  }

  get dateFirstFound() {
    return toTimestamp(this.data.dateFirstFound);
  }

  get isSeen() {
    return this.data.seen || false;
  }

  async updateSeen(seen) {
    return await this.update({
      seen: seen || false
    }, null, true);
  }

  get dateDetailsImported() {
    return toTimestamp(this.data.dateDetailsImported);
  }

  get status() {
    return this.data.status;
  }

  get details() {
    return this.data.listing || {};
  }

  get title() {
    return this.details.title;
  }

  get source() {
    return this.details.source;
  }

  get host() {
    return this.details.host;
  }

  get url() {
    return computeListingUrl({
      url: this.details.url,
      source: this.source
    });
  }

  get imageCount() {
    return (this.details.images || []).length;
  }

  get detailsImported() {
    return this.data.detailsImported;
  }

  get evidence() {
    return this.data.evidence || null;
  }

  get listingSource() {
    return getListingSourceInfo(this.source);
  }

  get iconUrl() {
    const src = this.listingSource;
    return src && src.iconUrl;
  }

  get latlng() {
    return extractListingLatLng(this.details);
  }

  get loadCount() {
    return this.data.loadCount || 1;
  }

  get dateLoadedFormatted() {
    const m = toMoment(this.data.dateLoaded);
    if (!m) {
      return null;
    } else {
      return m.fromNow();
      // .replace(/ seconds?/, ' s')
      // .replace(/ minutes?/, ' m')
      // .replace(/ hours?/, ' h')
      // .replace(/ days?/, ' d')
      // .replace(/ weeks?/, ' w')
      // .replace(/ month/, ' mo');
    }
  }

  get isInactive() {
    return this._get('status') === 'inactive';
  }

  //
  // TODO OLD STUFF COPIED OVER
  //

  get isActive() {
    return this._get('loadId') === this.city.lastLoadId;
  }

  get assessorUrl() {
    return this.city.recordsUrl;
  }

  get isPropertyVerified() {
    return !!this.verifiedPropertyId;
  }

  get verifiedPropertyId() {
    return this.data.verifiedPropertyId;
  }

  get verifiedProperty() {
    const data = this.data.verifiedPropertySnapshot || this.data.verifiedProperty;
    if (data) {
      return Property.dataConverter(this.city)({
        id: this.verifiedPropertyId,
        data: data
      });
    } else {
      return null;
    }
  }

  get dateVerified() {
    return toTimestamp(this._data.verifiedAt);
  }

  get verifiedByUser() {
    return this._data.verifiedBy;
  }

  get verifiedNotes() {
    return this._data.verifiedNotes;
  }

  get isAddressVerified() {
    return this.isPropertyVerified;
  }

  /**
   * TODO Start this was removed in test. Check to see if you can actually get rid of it after the merge.
   */
  get isWhitelisted() {
    return isListingWhitelisted({
      whitelisted: this._get('whitelisted') || false,
      whitelistExpiration: this.whitelistExpiration,
      now: new Date()
    });
  }

  get whitelistReason() {
    return this._get('whitelistReason') || '';
  }

  get whitelistExpiration() {
    const expiration = this._get('whitelistExpiration');
    return expiration ? toMoment(expiration) : null;
  }

  get whitelistUpdatedAt() {
    return toMoment(this._get('whitelistUpdatedAt'));
  }

  get whitelistUpdatedBy() {
    const user = this._get('whitelistUpdatedBy');
    return user ? new User(user) : null;
  }

  get notes() {
    return this._get('notes') || '';
  }
  /**
   * TODO End post-merge removal check.
   */

  get bestAddress() {
    const property = this.verifiedProperty;
    return property ? property.address : null;
  }

  get listingTitle() {
    return this._get('listing.title');
  }

  get listingSubtitle() {
    return this._get('listing.subtitle');
  }

  get listingPrice() {
    return this._get('listing.price');
  }

  get listingDescription() {
    return this._get('listing.description');
  }

  get listingHost() {
    return this._get('listing.host');
  }

  get listingSpecs() {
    return this._get('listing.specs');
  }

  get listingBedrooms() {
    return this._get('listing.bedrooms');
  }

  get listingBathrooms() {
    return this._get('listing.bathrooms');
  }

  get listingHalfBathrooms() {
    return this._get('listing.halfBathrooms');
  }

  get minNightStay() {
    return this._get('listing.minNightStay') || 1;
  }

  get images() {
    return (this._get('listing.images') || []).map(image => image.url || image);
  }

  get latLng() {
    return this.latlng;
  }

  get addressFormatted() {
    const property = this.verifiedProperty;
    return property ? property.addressFormatted : '';
  }

  get owners() {
    const property = this.verifiedProperty;
    return (property && property.owners) || [];
  }

  get ownerAddress() {
    const property = this.verifiedProperty;
    return (property && property.ownerAddress) || this.addressFormatted;
  }

  matches(query) {
    // TODO break query up into words, and test all n, then n-1, etc. Maybe include scoring to indicate how many
    // of the first n words matched.
    return listingMatchesQuery({
      query,
      addressFormatted: this.addressFormatted,
      title: this.listingTitle
    });
  }

  indexKeys() {
    return listingIndexKeys({
      addressFormatted: this.addressFormatted,
      title: this.listingTitle,
      host: this.listingHost
    });
  }

  async verifyProperty(property, notes) {
    this.verifying = true;
    try {
      const response = await postApi(`/v1/cities/${this.city.id}/listings/${this.id}/verification`, {
        propertyId: property?.id || null,
        notes: notes || null
      });
      if (response?.listing?.data) {
        this._data = response.listing.data;
        this.updateCount++;
      } else {
        await this.reload();
      }
    } finally {
      this.verifying = false;
    }
    return this;
  }

  updateNotes(notes) {
    return this.update({notes}, 'notes', true);
  }

  get primaryImageIndex() {
    let url = this._get('primaryImageUrl');
    let index = url ? this.images.indexOf(url) : -1;
    return index >= 0 ? index : (this._get('primaryImageIndex') || 0);
  }

  get primaryImage() {
    return this._get('primaryImageUrl') || this.images[this.primaryImageIndex];
  }

  get thumbnailUrl() {
    return this._get('thumbnailUrl') || null;
  }

  updatePrimaryImageIndex(index) {
    return this.update({
      primaryImageIndex: index,
      primaryImageUrl: this.images[index]
    }, true);
  }

  async updateActiveStatus(active) {
    const response = await listingRepository.updateListingStatus(this.city.id, this.id, {
      status: active ? 'active' : 'inactive'
    });
    if (response?.listing?.data) {
      this._data = {
        ...this._data,
        ...response.listing.data
      };
      this.updateCount++;
      return this;
    }
    return await this.reload();
  }

  fetchScreenshot() {
    return callApi('screenshot', {
      cityId: this.city.id,
      listingId: this.id
    })
  }

  /**
   * @return {Promise<ListingComment[]>}
   */
  async fetchComments() {
    return await ListingComment.list(this);
  }
}
